// Shared contract for the popup-OAuth handshake. The OAuth callback redirects
// the *popup* back to the SPA origin with `?<returnParam>=connected|error`;
// `postOAuthReturnFromPopup` (called from the app bootstrap before the full app
// mounts) relays that outcome to the opener tab and closes the popup, so the
// user's live chat thread never navigates away.

import { providerForReturnParams } from './providerRegistry';

export const OAUTH_RETURN_MESSAGE_SOURCE = 'oauth-return';

export interface OAuthReturnMessage {
  source: typeof OAUTH_RETURN_MESSAGE_SOURCE;
  provider: string;
  outcome: 'connected' | 'error';
}

export function isOAuthReturnMessage(value: unknown): value is OAuthReturnMessage {
  if (typeof value !== 'object' || value === null) return false;
  const msg = value as Record<string, unknown>;
  return (
    msg.source === OAUTH_RETURN_MESSAGE_SOURCE &&
    typeof msg.provider === 'string' &&
    (msg.outcome === 'connected' || msg.outcome === 'error')
  );
}

/**
 * If this document is an OAuth-return popup, relay the outcome to the opener and
 * close. Returns true when handled (the caller should NOT mount the full app).
 */
export function postOAuthReturnFromPopup(): boolean {
  if (!window.opener || window.opener === window) return false;
  let match: ReturnType<typeof providerForReturnParams>;
  try {
    match = providerForReturnParams(new URLSearchParams(window.location.search));
  } catch {
    return false;
  }
  if (!match) return false;
  const message: OAuthReturnMessage = {
    source: OAUTH_RETURN_MESSAGE_SOURCE,
    provider: match.provider.id,
    outcome: match.outcome as 'connected' | 'error',
  };
  try {
    window.opener.postMessage(message, window.location.origin);
  } catch {
    // Opener gone or cross-origin — fall through; the popup just closes.
  }
  window.close();
  return true;
}
